import dayjs from "dayjs";
import type { ApiResponse } from "@/api";
import { axiosInstance } from "@/api/axios-instance";
import { useGet } from "@/api/swr-hooks";
import type { YesNoUnknown } from "@/constants/femalePatientQuestionnaire";
import HelperManager from "@/helper/HelperManager";
import type { IDropDownOption, IPatientType, IRegistrationSetupItem } from "@/models";
import type {
  AllergyItem,
  generalConfig,
  IGetPatientsParams,
  IPatient,
  ISearchPatient,
} from "@/models/patient";
import { DATE_FORMATS } from "@/utils/date";
import {
  buildBillingUrl,
  buildClinicUrl,
  buildPatientUrl,
  buildPatientUrlwithClinic,
  requireClinicId,
} from "@/utils/serviceHelpers";

export interface SearchPatientParams {
  search: string;
  containMerged: boolean;
}

export interface ICreatePatientBody {
  patientTypeId: number;
  clinicId?: string;
  patientFullName: string;
  patientLastName: string;
  sex: string;
  dateOfBirth: string;
  idType: string;
  idNumber: string;
  countryOfBirth: string;
  nationality: string;
  mobileNumberRegionCode: string;
  mobileNumber: string;
  email: string;
  postalCode: string;
  addressLine1: string;
  addressLine2: string;
  unit: string;
  maritalStatus: string;
  language?: string;
  occupation?: string;
  race: string;
  religion?: string;
  residentialStatus?: string;
  allergyAlerts: string;
  alerts: string | null;
  allergies: string[];
  foodAllergies: string[];
  application: string;
  haveG6pdDeficiency: boolean;
  pregnancyStatus?: YesNoUnknown;
  lactationStatus?: YesNoUnknown;
  countryOfResidence: string;
  createdBy?: string;
  updatedBy?: string;
  nextOfKin: [
    {
      fullName: string;
      relationship: string;
      mobileNumber: string;
      mobileNumberRegionCode: string;
      email: string;
      isEmergencyContact: boolean;
    },
  ];
  drugAllergiesV2: AllergyItem[];
  appointmentId?: string;
}

export interface IPatientDetail extends ICreatePatientBody {
  id: string;
  isActive: boolean;
  isSoftDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  visited: boolean;
}

export interface IGetLocationFromPostalCodeResponse {
  street: string;
  building: string;
  unit: string;
  postalCode: string;
  country: string;
}

export interface ICreatePatientResponse {
  patientId: string;
  visitId: string;
  status: string;
}

export interface ICheckDuplicatePatientRequest {
  idType: string;
  idNumber: string;
  patientFullName: string;
  dateOfBirth: string;
  sex: string;
  postalCode: string;
}

export interface ISoftValidateIdAndMobileNumberRequest {
  idNumber: {
    exists: boolean;
    count: number;
  };
  mobileNumber: {
    exists: boolean;
    count: number;
  };
}

export interface MedicationHistoryItem {
  id: string;
  createdAt: string;
  visitId?: string;
  inventories: Array<{ name: string; quantity: number; uom: string }>;
}

export interface InvestigationHistoryItem {
  visitId: string;
  createdAt: string;
  investigations: Array<{ name: string }>;
}

export interface IMergeTwoPatientsRequest {
  sourcePatientId: string;
  targetPatientId: string;
  reason: string;
}
export interface IMergeTwoPatientsResponse {
  sourcePatientId: string;
  targetPatientId: string;
  status: string;
}

export interface IUpdateGeneralConfigResponse {
  message: string;
  data: generalConfig;
}

const patientService = {
  getPatients: async (params: IGetPatientsParams): Promise<ApiResponse<ISearchPatient[]>> => {
    const searchBody: SearchPatientParams = {
      search: params.searchValue || "",
      containMerged: params.containMerged,
    };

    const queryParams: Record<string, string | number> = {
      limit: params.limit || 20,
      skip: params.page ? (params.page - 1) * (params.limit || 20) : 0,
    };

    if (params.sortBy && params.sortOrder) {
      queryParams.sort = `${params.sortBy}:${params.sortOrder}`;
    }
    if (params.patientStatus) {
      if (params.patientStatus.active && !params.patientStatus.inactive) {
        queryParams.status = "active";
      } else if (!params.patientStatus.active && params.patientStatus.inactive) {
        queryParams.status = "inactive";
      } else {
        delete queryParams.status;
      }
    }

    const clinicId = requireClinicId();
    const response = await axiosInstance.post(
      buildPatientUrl(`/patients/search/${clinicId}`),
      searchBody,
      { params: queryParams },
    );

    return response.data;
  },

  getPatientById: async (
    query: string,
    patientId: string | undefined,
  ): Promise<ApiResponse<IPatient>> => {
    const url = buildPatientUrl(`/patients/${patientId}?includeRelated=${query}`);
    const response = await axiosInstance.get(url);
    return response.data;
  },

  createPatient: async (body: ICreatePatientBody): Promise<ApiResponse<ICreatePatientResponse>> => {
    body.clinicId = requireClinicId();
    body.application = "Echo";
    if (!body.foodAllergies) {
      body.foodAllergies = [];
    }
    const response = await axiosInstance.post(buildPatientUrl("/patients"), body);
    return response.data;
  },

  updatePatient: async (
    body: ICreatePatientBody,
    patientId: string,
    options: { isNetQ?: boolean; queueId?: string } = {},
  ): Promise<ApiResponse<ICreatePatientResponse>> => {
    body.clinicId = requireClinicId();
    body.dateOfBirth = dayjs(body.dateOfBirth).format(DATE_FORMATS.ISO);
    // const cleanBody = HelperManager.omitNullValues(body);
    const queryParams = HelperManager.objectToQueryString(options);
    if (!body.foodAllergies) {
      body.foodAllergies = [];
    }
    const response = await axiosInstance.put(
      buildPatientUrl(`/patients/${patientId}?${queryParams}`),
      body,
    );
    return response.data;
  },
  updatePatientFromAppointmenPage: async (
    body: any,
    patientId: string,
  ): Promise<ApiResponse<ICreatePatientResponse>> => {
    body.clinicId = requireClinicId();
    body.dateOfBirth = dayjs(body.dateOfBirth).format(DATE_FORMATS.ISO);
    const cleanBody = HelperManager.omitNullValues(body);
    const response = await axiosInstance.put(buildPatientUrl(`/patients/${patientId}`), cleanBody);
    return response.data;
  },

  getLocationFromPostalCode: async (
    postalCode: string,
    countryCode: string,
  ): Promise<ApiResponse<IGetLocationFromPostalCodeResponse[]>> => {
    const url = `/api/patient/address/${countryCode}/postal-code/${postalCode}`;
    const response = await axiosInstance.get(url, { skip404Redirect: true } as any);
    return response.data;
  },

  getPatientType: async (): Promise<ApiResponse<IPatientType[]>> => {
    const clinicId = requireClinicId();
    const url = `/api/patient/clinics/${clinicId}/patient-types`;
    const response = await axiosInstance.get(url);
    return response.data;
  },

  getNehrData: async (
    params: string[],
  ): Promise<
    ApiResponse<{
      races: IDropDownOption[];
      religions: IDropDownOption[];
      languages: IDropDownOption[];
      nationalities: IDropDownOption[];
      maritalStatuses: IDropDownOption[];
      occupations: IDropDownOption[];
      residentialStatuses: IDropDownOption[];
      countryCodes: IDropDownOption[];
    }>
  > => {
    const url = `/api/patient/patient-form-dropdown-options`;
    const response = await axiosInstance.get(url, {
      params: {
        query: params,
      },
    });
    return response.data;
  },

  checkDuplicateIdNumber: async (
    body: ICheckDuplicatePatientRequest,
  ): Promise<ApiResponse<ISearchPatient[]>> => {
    const response = await axiosInstance.post(buildPatientUrl("/patients/query"), body);
    return response.data;
  },

  deactivatePatient: async (patientId: string): Promise<ApiResponse<ICreatePatientResponse>> => {
    const response = await axiosInstance.patch(
      buildPatientUrl(`/patients/${patientId}/deactivate`),
    );
    return response.data;
  },

  updatePatientAllergyAlert: async (
    patientId: string,
    alerts: string,
  ): Promise<ApiResponse<ICreatePatientResponse>> => {
    const response = await axiosInstance.patch(
      buildPatientUrl(`/patients/${patientId}/allergy-alert`),
      { alerts },
    );
    return response.data;
  },
  softValidateIdAndMobileNumber: async (body: {
    idNumber?: string;
    mobileNumberRegionCode?: string;
    mobileNumber?: string;
    excludePatientId?: string;
  }): Promise<ApiResponse<ISoftValidateIdAndMobileNumberRequest>> => {
    const cleanBody = HelperManager.omitNullValues(body);
    const response = await axiosInstance.post(
      buildPatientUrl("/patients/validate-id-mobile"),
      cleanBody,
    );
    return response.data;
  },

  getTotalOutstanding: async (patientId: string) => {
    const response = await axiosInstance.get<ApiResponse<number>>(
      buildBillingUrl(`/patients/${patientId}/total-outstanding`),
    );
    return response.data;
  },

  getMedicationHistory: async (patientId: string) => {
    const response = await axiosInstance.get<ApiResponse<MedicationHistoryItem[]>>(
      buildClinicUrl(`/patients/${patientId}/visit-medication`, "/api/outpatient"),
    );
    return response.data;
  },

  getInvestigationHistory: async (patientId: string) => {
    const response = await axiosInstance.get<ApiResponse<InvestigationHistoryItem[]>>(
      buildClinicUrl(`/patients/${patientId}/visit-investigation`, "/api/outpatient"),
    );
    return response.data;
  },
  searchAllergies: async (search: string) => {
    const response = await axiosInstance.get<ApiResponse<{ code: string; description: string }[]>>(
      `/api/patient/drugs/search?limit=50&q=${search}`,
    );
    return response.data;
  },
  mergeTwoPatients: async (body: IMergeTwoPatientsRequest) => {
    const response = await axiosInstance.post<ApiResponse<IMergeTwoPatientsResponse>>(
      buildPatientUrl(`/patients/merge`),
      body,
    );
    return response.data;
  },

  updateGeneralConfig: async (
    configId: string,
    body: { allowUpdateAllergiesDate: boolean; version: number },
  ) => {
    const response = await axiosInstance.put<ApiResponse<IUpdateGeneralConfigResponse>>(
      buildPatientUrlwithClinic(`/general-config/${configId}`),
      body,
    );
    return response.data;
  },

  getGeneralConfig: async () => {
    const response = await axiosInstance.get<ApiResponse<generalConfig>>(
      buildPatientUrlwithClinic(`/general-config`),
    );
    return response.data;
  },

  useGeneralConfig: () => {
    const result = useGet<ApiResponse<generalConfig>>(buildPatientUrlwithClinic("/general-config"));
    return {
      ...result,
      data: result.data?.data,
    };
  },
  getPreRegistrationSetup: async (type: string) => {
    const url = `/api/patient/clinic/${requireClinicId()}/type/${type}/patient-registration-setting?limit=100`;
    const response = await axiosInstance.get<ApiResponse<IRegistrationSetupItem[]>>(url);
    return response.data;
  },
  updatePreRegistrationSetup: async (type: string, body: IRegistrationSetupItem) => {
    const url = `/api/patient/clinic/${requireClinicId()}/type/${type}/patient-registration-setting-update`;
    const response = await axiosInstance.patch<ApiResponse<IRegistrationSetupItem>>(url, body);
    return response.data;
  },
};

export const useGeneralConfig = patientService.useGeneralConfig;
export default patientService;
